import { useEffect, useRef, useState } from "react";
import { Sun, Moon, Monitor } from "lucide-react";

// Darkmodus für Partner, Admin und Fahrer: Hell / Auto (folgt dem Gerät) / Dunkel. Auswahl bleibt im Browser gespeichert.
// Desktop: runder Button unten rechts, klappt nach oben zu einer schmalen Leiste mit den drei Symbolen auf.
// Handy: der Button ist ausgeblendet, der Schalter steckt in der Navigation (ThemeSwitchInline in den Menüs).
const KEY = "gt24_theme";
const HOSTS = ["partners.gotransfer24.de", "driver.gotransfer24.de", "admin.gotransfer24.de"];
// Schlichte Linien-Symbole (wie im restlichen Menü): Sonne = Hell, Monitor = Auto (folgt dem Gerät), Mond = Dunkel
const MODES = [["light", Sun, "Hell"], ["auto", Monitor, "Auto"], ["dark", Moon, "Dunkel"]];
const ICON = { size: 18, strokeWidth: 2, "aria-hidden": true };
const readMode = () => { try { const v = localStorage.getItem(KEY); return MODES.some(([m]) => m === v) ? v : "light"; } catch { return "light"; } };
const mql = () => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(prefers-color-scheme: dark)") : null);
const apply = (mode) => {
  const dark = mode === "dark" || (mode === "auto" && !!mql()?.matches);
  document.documentElement.setAttribute("data-theme", dark ? "dark" : "light");
};
const listeners = new Set();
let current = typeof document !== "undefined" ? readMode() : "light";
const setGlobalMode = (m) => {
  current = m;
  try { localStorage.setItem(KEY, m); } catch { /* Speicher nicht verfügbar: gilt nur für diese Sitzung */ }
  apply(m);
  listeners.forEach((fn) => fn(m));
};

// Sofort beim Laden anwenden (vermeidet kurzes Aufblitzen der hellen Seite) – nur Partner/Admin/Fahrer, nie die öffentliche Seite (react-snap)
if (typeof document !== "undefined" && HOSTS.includes(window.location.hostname)) apply(current);

function useThemeMode() {
  const [mode, setMode] = useState(current);
  useEffect(() => { listeners.add(setMode); return () => listeners.delete(setMode); }, []);
  // „Auto“: auf Änderungen der Geräte-Einstellung reagieren
  useEffect(() => {
    const m = mql();
    if (mode !== "auto" || !m) return undefined;
    const onChange = () => apply("auto");
    m.addEventListener ? m.addEventListener("change", onChange) : m.addListener(onChange);
    return () => (m.removeEventListener ? m.removeEventListener("change", onChange) : m.removeListener(onChange));
  }, [mode]);
  return [mode, setGlobalMode];
}

/** Reihe aus drei Symbol-Knöpfen für die mobile Navigation */
export function ThemeSwitchInline({ style, dark }) {
  const [mode, setMode] = useThemeMode();
  return (
    <div className={`gt-theme-inline${dark ? " gt-theme-inline--dark" : ""}`} role="group" aria-label="Farbschema" style={style}>
      {MODES.map(([m, Icon, label]) => (
        <button key={m} type="button" aria-pressed={mode === m} title={label} aria-label={label} onClick={() => setMode(m)}><Icon {...ICON} /></button>
      ))}
    </div>
  );
}

export default function ThemeSwitch() {
  const [mode, setMode] = useThemeMode();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", away); document.addEventListener("touchstart", away); document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("touchstart", away); document.removeEventListener("keydown", esc); };
  }, [open]);
  const cur = MODES.find(([m]) => m === mode) || MODES[0];
  return (
    <div className="gt-theme-fab" ref={ref}>
      {open && (
        <div className="gt-theme-fab__menu" role="group" aria-label="Farbschema">
          {MODES.map(([m, Icon, label]) => (
            <button key={m} type="button" aria-pressed={mode === m} title={label} aria-label={label} onClick={() => { setMode(m); setOpen(false); }}><Icon {...ICON} /></button>
          ))}
        </div>
      )}
      <button type="button" className="gt-theme-fab__btn" aria-expanded={open} aria-label={`Farbschema: ${cur[2]}`} title={`Farbschema: ${cur[2]}`} onClick={() => setOpen((o) => !o)}>{(() => { const Cur = cur[1]; return <Cur {...ICON} />; })()}</button>
    </div>
  );
}
